import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { localDayNumber } from "@/lib/tool-kit/astronomy/zone";

/** Hours and whole minutes of a span in seconds, rounded to the nearest minute. */
export function hoursAndMinutes(seconds: number): {
  hours: number;
  minutes: number;
} {
  const total = Math.round(seconds / 60);
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

/** The clock time of an instant in a zone, rounded to the minute. */
export function clockInZone(
  locale: string,
  ms: number,
  timeZone: string
): string {
  const rounded = Math.round(ms / 60_000) * 60_000;
  return formatDateTime(locale, new Date(rounded), {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * A clock time that says so when it falls on another calendar day than `localDay`:
 * `nextDay` and `previousDay` are templates with a `{time}` slot.
 */
export function clockWithDay(
  locale: string,
  ms: number,
  timeZone: string,
  localDay: number,
  templates: { nextDay: string; previousDay: string }
): string {
  const rounded = Math.round(ms / 60_000) * 60_000;
  const time = clockInZone(locale, rounded, timeZone);
  const day = localDayNumber(rounded, timeZone);
  if (day > localDay) {
    return t(templates.nextDay, { time });
  }
  if (day < localDay) {
    return t(templates.previousDay, { time });
  }
  return time;
}

/** A calendar day (as a day number) written long in the reader's language. */
export function longDay(locale: string, dayNumberValue: number): string {
  return formatDateTime(locale, new Date(dayNumberValue * 86_400_000), {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
